// SPDX-FileCopyrightText: 2026 Igor Brkic <igor@hyperglitch.com>
//
// SPDX-License-Identifier: GPL-3.0-or-later

/**
 * Coarse uniform spatial grid for region queries (spec §6.2 "a coarse spatial
 * grid for region queries").
 *
 * The grid is an accelerator only: every query returns a *superset* of the true
 * answer and callers re-test exactly. Objects with an unknown extent are held
 * in a separate bucket and returned by every query, because "we don't know
 * where it is" must never mean "it isn't here".
 */

import { intersects, type BBox } from './bbox.ts';

export interface GridItem {
  bbox: BBox | null;
}

const DEFAULT_CELL_MM = 10;
const MAX_CELLS = 250_000;

export class SpatialGrid<T extends GridItem = GridItem> {
  private readonly cell: number;
  private readonly originX: number;
  private readonly originY: number;
  private readonly cols: number;
  private readonly rows: number;
  private readonly buckets: T[][];
  /** Objects with no knowable extent; always included in query results. */
  readonly unlocated: T[];

  private constructor(cell: number, originX: number, originY: number, cols: number, rows: number) {
    this.cell = cell;
    this.originX = originX;
    this.originY = originY;
    this.cols = cols;
    this.rows = rows;
    this.buckets = Array.from({ length: cols * rows }, () => [] as T[]);
    this.unlocated = [];
  }

  static build<T extends GridItem>(items: readonly T[], cellSize = DEFAULT_CELL_MM): SpatialGrid<T> {
    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;
    for (const it of items) {
      if (it.bbox === null) continue;
      if (it.bbox.minX < minX) minX = it.bbox.minX;
      if (it.bbox.minY < minY) minY = it.bbox.minY;
      if (it.bbox.maxX > maxX) maxX = it.bbox.maxX;
      if (it.bbox.maxY > maxY) maxY = it.bbox.maxY;
    }
    if (minX === Infinity) {
      minX = 0;
      minY = 0;
      maxX = 0;
      maxY = 0;
    }

    let cell = Math.max(cellSize, 0.001);
    let cols = Math.max(1, Math.ceil((maxX - minX) / cell) + 1);
    let rows = Math.max(1, Math.ceil((maxY - minY) / cell) + 1);
    // Guard against pathological coordinates blowing up memory.
    while (cols * rows > MAX_CELLS) {
      cell *= 2;
      cols = Math.max(1, Math.ceil((maxX - minX) / cell) + 1);
      rows = Math.max(1, Math.ceil((maxY - minY) / cell) + 1);
    }

    const grid = new SpatialGrid<T>(cell, minX, minY, cols, rows);
    for (const it of items) grid.insert(it);
    return grid;
  }

  private colOf(x: number): number {
    return Math.min(this.cols - 1, Math.max(0, Math.floor((x - this.originX) / this.cell)));
  }

  private rowOf(y: number): number {
    return Math.min(this.rows - 1, Math.max(0, Math.floor((y - this.originY) / this.cell)));
  }

  private insert(item: T): void {
    if (item.bbox === null) {
      this.unlocated.push(item);
      return;
    }
    const c0 = this.colOf(item.bbox.minX);
    const c1 = this.colOf(item.bbox.maxX);
    const r0 = this.rowOf(item.bbox.minY);
    const r1 = this.rowOf(item.bbox.maxY);
    for (let r = r0; r <= r1; r++) {
      for (let c = c0; c <= c1; c++) {
        this.buckets[r * this.cols + c]!.push(item);
      }
    }
  }

  /** Superset of the items whose box intersects `query`. Re-test exactly. */
  candidates(query: BBox): T[] {
    const seen = new Set<T>(this.unlocated);
    const c0 = this.colOf(query.minX);
    const c1 = this.colOf(query.maxX);
    const r0 = this.rowOf(query.minY);
    const r1 = this.rowOf(query.maxY);
    for (let r = r0; r <= r1; r++) {
      for (let c = c0; c <= c1; c++) {
        for (const item of this.buckets[r * this.cols + c]!) seen.add(item);
      }
    }
    return [...seen];
  }

  /** Exact: items whose box actually intersects `query`. Unlocated are excluded. */
  query(box: BBox, tol = 0): T[] {
    return this.candidates(box).filter((it) => it.bbox !== null && intersects(it.bbox, box, tol));
  }
}
